import { useCallback, useEffect, useState } from 'react';
import { useAccessToken } from '../auth/use-access-token.js';
import { apiFetch } from './client.js';

/**
 * A family's two parents (canvas row D, approved 17 October). Either parent
 * reads who they are; the first parent invites, sends again and removes. The
 * link's claim sends the hash, never the link, as stage C's does.
 *
 * Refusals come back as ApiError: has_other_parent, email_has_account,
 * email_taken, not_invited; on the link, link_dead, other_email (with the
 * address in `email`) and has_account.
 */

type GetToken = () => Promise<string | null>;

export interface OtherParent {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  sent_at: string | null;
  joined_at: string | null;
}

export interface Parents {
  you: 'holder' | 'second';
  holder: { first_name: string; last_name: string; email: string | null };
  other: OtherParent | null;
}

export type ParentsState = { status: 'loading' } | { status: 'ready'; parents: Parents } | { status: 'failed' };

export function useParents(): { state: ParentsState; reload: () => void } {
  const getToken = useAccessToken();
  const [state, setState] = useState<ParentsState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);
  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    apiFetch<Parents>('/api/parents', getToken)
      .then((parents) => {
        if (!cancelled) setState({ status: 'ready', parents });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [getToken, nonce]);

  return { state, reload };
}

export function inviteSecondParent(
  getToken: GetToken,
  invite: { firstName: string; lastName: string; email: string },
): Promise<{ id: string }> {
  return apiFetch('/api/second-parents', getToken, {
    method: 'POST',
    body: JSON.stringify({ first_name: invite.firstName, last_name: invite.lastName, email: invite.email }),
  });
}

export function sendSecondParentAgain(getToken: GetToken, id: string): Promise<void> {
  return apiFetch(`/api/second-parents/${encodeURIComponent(id)}/invitations`, getToken, { method: 'POST' });
}

export function removeSecondParent(getToken: GetToken, id: string): Promise<{ removed_at: string }> {
  return apiFetch(`/api/second-parents/${encodeURIComponent(id)}`, getToken, { method: 'DELETE' });
}

export function claimSecondParent(getToken: GetToken, hash: string): Promise<Record<string, never>> {
  return apiFetch(`/api/second-parent-invitations/${hash}/claims`, getToken, { method: 'POST' });
}
